<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';
import { z } from 'zod';

import ModalForm from '~/components/shared/ModalForm.vue';
import { RefundOrigin } from '~/services/receipt-model-api/receipt-model-api.types';

import { REFUND_ORIGIN_LABEL, REFUND_ORIGIN_OPTIONS } from '../constants';

interface Props {
  fieldLabel?: string;
  isLoading?: boolean;
  maxLength?: number;
  minLength?: number;
  modelValue: boolean;
  placeholder: string;
  primaryButtonText: string;
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  fieldLabel: 'Motivo',
  isLoading: false,
  maxLength: 255,
  minLength: 10,
  title: 'Estorno',
});

const emit = defineEmits<{
  'confirm': [data: { origin: RefundOrigin; reason: string }];
  'update:modelValue': [value: boolean];
}>();

const refundSchema = z.object({
  origin: z.enum(['LTDA', 'ORIGIN'], {
    errorMap: () => ({ message: 'Selecione a origem do estorno' }),
  }),
  reason: z
    .string()
    .min(1, 'Motivo é obrigatório')
    .min(props.minLength, `Motivo deve ter pelo menos ${props.minLength} caracteres`)
    .max(props.maxLength, `Motivo deve ter no máximo ${props.maxLength} caracteres`)
    .refine(
      (val) => val.trim().length >= props.minLength,
      `Motivo deve ter pelo menos ${props.minLength} caracteres válidos`,
    ),
});

type RefundFormData = z.infer<typeof refundSchema>;

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setFieldError,
} = useForm<RefundFormData>({
  initialValues: {
    origin: undefined as unknown as RefundOrigin,
    reason: '',
  },
  validateOnMount: false,
  validationSchema: toTypedSchema(refundSchema),
});

const [origin] = defineField('origin');
const [reason, reasonProps] = defineField('reason');

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const characterCount = computed(() => reason.value?.length || 0);

const isFormValid = computed(() => refundSchema.safeParse({
  origin: origin.value,
  reason: reason.value,
}).success);

const clearReasonError = () => setFieldError('reason', undefined);

const validateReason = async () => {
  try {
    setFieldError('reason', undefined);
    await refundSchema.shape.reason.parseAsync(reason.value);
  } catch (error: any) {
    const fieldError = error.errors?.[0];
    if (fieldError) setFieldError('reason', fieldError.message);
  }
};

const handleSave = handleSubmit((formValues) => {
  emit('confirm', { origin: formValues.origin, reason: formValues.reason.trim() });
});

const handleCancel = () => {
  isVisible.value = false;
};

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    resetForm();
  }
});
</script>

<template>
  <ModalForm
    v-model="isVisible"
    :title="title"
    mode="create"
    :loading="isLoading"
    :primary-button-text="primaryButtonText"
    :primary-button-disabled="!isFormValid"
    size="md"
    @save="handleSave"
    @cancel="handleCancel"
  >
    <template #default="{ isViewMode, isLoading: modalIsLoading }">
      <div class="form-group mb-3">
        <label class="form-label required">{{ REFUND_ORIGIN_LABEL }}</label>
        <div class="form-selectgroup form-selectgroup-boxes d-flex flex-column">
          <label
            v-for="option in REFUND_ORIGIN_OPTIONS"
            :key="option.value"
            class="form-selectgroup-item flex-fill"
          >
            <input
              v-model="origin"
              type="radio"
              :value="option.value"
              class="form-selectgroup-input"
              :disabled="isViewMode || modalIsLoading"
            />
            <div class="form-selectgroup-label d-flex align-items-center p-3">
              <div class="me-3">
                <span class="form-selectgroup-check" />
              </div>
              <div class="form-selectgroup-label-content">
                <div class="font-weight-medium">{{ option.description }}</div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label required" for="reason">
          {{ fieldLabel }}
        </label>
        <textarea
          id="reason"
          v-model="reason"
          v-bind="reasonProps"
          class="form-control"
          :class="{ 'is-invalid': errors.reason }"
          :disabled="isViewMode || modalIsLoading"
          :placeholder="placeholder"
          rows="4"
          :maxlength="maxLength"
          @input="clearReasonError"
          @blur="validateReason"
        />
        <div class="d-flex justify-content-between align-items-start gap-2 mt-1">
          <div v-if="errors.reason" class="invalid-feedback d-block mb-0">
            {{ errors.reason }}
          </div>
          <small class="text-muted ms-auto character-count">
            {{ characterCount }}/{{ maxLength }} caracteres
          </small>
        </div>
      </div>
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.form-control {
  resize: vertical;
  min-height: 100px;
}

.invalid-feedback {
  font-size: 0.875rem;
}

.character-count {
  flex: 0 0 auto;
  white-space: nowrap;
}
</style>
